/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { OrderTemplate, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import getFullOrderTemplates, { GetFullOrderTemplatesInput } from './get-full-order-templates';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetOrderTemplatesPreviewInput implements IActionInput {
    public queryResultSettings: IQueryResultSettings;

    constructor(queryResultSettings: IQueryResultSettings) {
        this.queryResultSettings = queryResultSettings;
        this.queryResultSettings.count = true;
        this.queryResultSettings.Paging!.Top = 4;
    }

    public getCacheKey = () => 'GetOrderTemplatesPreview';

    public getCacheObjectType = () => 'GetOrderTemplatesPreview';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 */
export const createGetOrderTemplatesPreviewInput = (inputData: ICreateActionContext): GetOrderTemplatesPreviewInput => {
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    return new GetOrderTemplatesPreviewInput(queryResultSettings);
};

/**
 * The action method for the GetOrderTemplatesPreview data action.
 * @param input The action input.
 * @param context The action context.
 */
export async function getOrderTemplatesPreview(input: GetOrderTemplatesPreviewInput, context: IActionContext): Promise<OrderTemplate[]> {
    const response: OrderTemplate[] = [];
    if (!context.requestContext.user.isAuthenticated) {
        context.trace('Unable to get order templates preview. User is not authenticated.');
        return Promise.resolve(response);
    }
    const fullOrderTemplates = await getFullOrderTemplates(
        new GetFullOrderTemplatesInput(input.queryResultSettings, false, false),
        context
    );
    return fullOrderTemplates.sort((a, b) => (a.productList.Name || '').localeCompare(b.productList.Name || ''));
}

/**
 * The GetOrderTemplatesPreview Data Action
 * Returns order templates.
 */
export const getOrderTemplatesPreviewDataAction = createObservableDataAction<OrderTemplate[]>({
    id: 'defa/retail-actions/order-templates/get-order-templates-preview',
    action: <IAction<OrderTemplate[]>>getOrderTemplatesPreview,
    input: createGetOrderTemplatesPreviewInput
});

export default getOrderTemplatesPreviewDataAction;
